import { Cloud, Menu } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { PostFilter, type Hashtag, type Keyword } from '../../../../entities/post/models';
import { useFilteredPostsNavigation } from '../../filtered-posts/hook/use-filtered-posts-navigation';
import { useFilteredProfilesNavigation } from '../../filtered-profiles/hook/use-filtered-profiles-navigation';
import { useHashtags } from '../hooks/use-hashtags';
import { useKeywords } from '../hooks/use-keywords';
import { showDrillDownContextMenu } from '../overlay/show-drill-down-context-menu';

import { DataTable, type TableColumnConfig } from '@/shared/components/data-table';
import { PageNavigation } from '@/shared/components/page-navigation/page-navigation';
import { SectionHeader } from '@/shared/components/section-header';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/shadcn/tabs';
import { Stage } from '@/shared/components/stage/stage';
import { StaticTable } from '@/shared/components/static-table/static-table';
import { useStaticTable } from '@/shared/components/static-table/use-static-table';
import { WordCloud } from '@/shared/components/word-cloud/word-cloud';
import { useSelectedSavedQuery } from '@/shared/hooks/use-selected-saved-query';
import { useSharedDateRange } from '@/shared/hooks/use-shared-date-range';
import { usePublicFilter } from '../../shared/public-filter/use-public-filter';

interface Props {
  className?: string;
}

enum VIEW_TYPE {
  LIST = 'list',
  WORD_CLOUD = 'word-cloud',
}

enum TARGET_DATA {
  KEYWORDS = 'keywords',
  HASHTAGS = 'hashtags',
}

export function WordCloudSection({ className }: Props) {
  const [viewType, setViewType] = useState<VIEW_TYPE>(VIEW_TYPE.WORD_CLOUD);
  const [targetData, setTargetData] = useState<TARGET_DATA>(TARGET_DATA.KEYWORDS);

  return (
    <div className={className}>
      <SectionHeader
        title="Related keywords and hashtags"
        description="Find the key terms and hashtags that drive conversations"
      />
      <div className="p-6">
        <div className="rounded-lg border bg-white shadow-sm">
          <div className="flex justify-between p-6">
            <Select
              value={targetData}
              onValueChange={(value) => setTargetData(value as TARGET_DATA)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a word" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TARGET_DATA.KEYWORDS}>Keywords</SelectItem>
                <SelectItem value={TARGET_DATA.HASHTAGS}>Hashtags</SelectItem>
              </SelectContent>
            </Select>
            <Tabs value={viewType} onValueChange={(value) => setViewType(value as VIEW_TYPE)}>
              <TabsList>
                <TabsTrigger value={VIEW_TYPE.LIST}>
                  <Menu />
                  List
                </TabsTrigger>
                <TabsTrigger value={VIEW_TYPE.WORD_CLOUD}>
                  <Cloud />
                  Word cloud
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <DataSection viewType={viewType} targetData={targetData} />
        </div>
      </div>
    </div>
  );
}

const DataSection = ({
  viewType,
  targetData,
}: {
  viewType: VIEW_TYPE;
  targetData: TARGET_DATA;
}) => {
  const navigateToFilteredPosts = useFilteredPostsNavigation();
  const navigateToFilteredProfiles = useFilteredProfilesNavigation();

  const { query: selectedQuery } = useSelectedSavedQuery();
  const { dateRange } = useSharedDateRange();
  const { publicFilter } = usePublicFilter(selectedQuery.id);
  // All hooks must be called before any early returns
  const keywordsQuery = useKeywords(
    {
      queryId: selectedQuery?.id || '',
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      publicFilter,
    },
    targetData === TARGET_DATA.KEYWORDS,
  );

  const hashtagsQuery = useHashtags(
    {
      queryId: selectedQuery?.id || '',
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      publicFilter,
    },
    targetData === TARGET_DATA.HASHTAGS,
  );

  const currentQuery = useMemo(() => {
    if (targetData === TARGET_DATA.KEYWORDS) return keywordsQuery;
    return hashtagsQuery;
  }, [keywordsQuery, hashtagsQuery, targetData]);

  const words = useMemo(() => {
    return currentQuery.data
      ?.map((item) => ({
        text: item.name,
        value: item.post_volume,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 50);
  }, [currentQuery]);

  const onRowClick = useCallback(
    async (keyword: Keyword, event: React.MouseEvent) => {
      const action = await showDrillDownContextMenu({
        position: { x: event.clientX, y: event.clientY },
      });

      const filterName =
        targetData === TARGET_DATA.KEYWORDS ? PostFilter.Keyword : PostFilter.Hashtag;

      switch (action) {
        case 'view-posts':
          await navigateToFilteredPosts({
            filter: {
              [filterName]: keyword.name,
            },
            dimension: filterName,
          });
          break;
        case 'view-profiles':
          await navigateToFilteredProfiles({
            filter: {
              [filterName]: keyword.name,
            },
            dimension: filterName,
          });
          break;
        case 'close':
          break;
      }
    },
    [navigateToFilteredPosts, navigateToFilteredProfiles, targetData],
  );

  // Early return if dependencies are not ready
  if (!selectedQuery?.id || !dateRange.startDate || !dateRange.endDate) {
    return (
      <div className="flex h-104 items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground text-lg font-medium">Loading...</p>
          <p className="text-muted-foreground text-sm">Waiting for query and date range data</p>
        </div>
      </div>
    );
  }

  return (
    <Stage className="h-104 w-full">
      <Stage.Scene status={currentQuery.isLoading}>
        <div className="flex h-104 items-center justify-center">
          <div className="text-center">
            <p className="text-muted-foreground text-lg font-medium">Loading...</p>
          </div>
        </div>
      </Stage.Scene>
      <Stage.Scene status={!currentQuery.data || currentQuery.data.length === 0}>
        <div className="flex h-104 items-center justify-center">
          <div className="text-center">
            <p className="text-muted-foreground text-lg font-medium">No data available</p>
          </div>
        </div>
      </Stage.Scene>

      <Stage.Scene status={viewType === VIEW_TYPE.WORD_CLOUD}>
        <WordCloud
          words={words ?? []}
          isLoading={currentQuery.isLoading}
          prefix={targetData === TARGET_DATA.HASHTAGS ? '#' : undefined}
          onWordClick={(word, _, event) => {
            onRowClick(
              {
                name: word.text,
                post_volume: Math.floor(word.value as number),
              },
              event,
            );
          }}
        />
      </Stage.Scene>
      <Stage.Scene status={viewType === VIEW_TYPE.LIST}>
        <StaticTableSection
          isLoading={currentQuery.isLoading}
          data={currentQuery.data}
          targetData={targetData}
          onRowClick={onRowClick}
        />
      </Stage.Scene>
    </Stage>
  );
};

const hastagColumns: TableColumnConfig<Hashtag>[] = [
  {
    key: 'name',
    header: 'Hashtag',
    size: '1fr',
    cell: ({ getValue }) => {
      return <DataTable.Cell getValue={getValue} />;
    },
  },
  {
    key: 'post_volume',
    header: 'Volume',
    size: '100px',
    cell: ({ getValue }) => {
      return <DataTable.Cell getValue={getValue} />;
    },
  },
];

const keywordColumns: TableColumnConfig<Keyword>[] = [
  {
    key: 'name',
    header: 'Keyword',
    size: '1fr',
    cell: ({ getValue }) => {
      return <DataTable.Cell getValue={getValue} />;
    },
  },
  {
    key: 'post_volume',
    header: 'Volume',
    size: '100px',
    cell: ({ getValue }) => {
      return <DataTable.Cell getValue={getValue} />;
    },
  },
];

const StaticTableSection = ({
  data,
  targetData,
  onRowClick,
  isLoading,
}: {
  data?: Keyword[];
  isLoading: boolean;
  targetData: TARGET_DATA;
  onRowClick: (data: Keyword, event: React.MouseEvent) => void;
}) => {
  const pageSize = 5;
  const { table, gridTemplateColumns } = useStaticTable({
    data: data || [],
    columns: targetData === TARGET_DATA.KEYWORDS ? keywordColumns : hastagColumns,
    pageSize,
  });

  const showPagination = data ? data.length > pageSize : false;

  return (
    <>
      <div className="p-6 pt-0">
        <StaticTable
          isLoading={isLoading}
          table={table}
          gridTemplateColumns={gridTemplateColumns}
          onRowClick={onRowClick}
          pageSize={5}
        />
      </div>
      {showPagination && <PageNavigation className="border-base-border border-t" table={table} />}
    </>
  );
};
